import { Injectable, Inject } from '@angular/core';
import { ContentfulClientApi, createClient, Entry, EntryCollection } from 'contentful';
import { get, Dictionary } from 'lodash';
import { defer, from, Observable, of, Subject } from 'rxjs';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { map, startWith, switchMap } from 'rxjs/operators';

import { ContentTypesEnum, ContentTypesEnumType } from '@enums/content-type.enum';
import { ContentfulService } from '../contentful/contentful.service';
import { ContentfulCacheService, ContentfulPayloadObject } from '@airtradeit/utils';
import { ConfigService } from '@services/config/config.service';
import { Store } from '@ngrx/store';
import { AppState } from '@store/state';
import * as errorModalActions from '@store/error-modal/error-modal.actions';
import { ContentFieldsEnum } from '@enums/content-fields.enum';
import { ErrorContent } from './_interfaces_/content.interface';
import { ErrorModalContent } from '@store/error-modal/error-modal.model';

@Injectable({
  providedIn: 'root',
})
export class ContentService {
  public contentfulClient: ContentfulClientApi;
  detectLanguageChange$: Subject<string> = new Subject<string>();
  public contentTypesForBothModes: string[] = [];

  constructor(
    @Inject(TranslateService) public _translateService: TranslateService,
    @Inject(ContentfulService) public _contentfulService: ContentfulService,
    @Inject(ConfigService) private configService: ConfigService,
    @Inject(ContentfulCacheService) private contentfulCacheService: ContentfulCacheService,
    @Inject(Store) private _store: Store<AppState>
  ) {
    this.contentfulClient = createClient({
      space: this.configService.getProperty<string>('CONTENTFUL_SPACE'),
      accessToken: this.configService.getProperty<string>('CONTENTFUL_TOKEN'),
      host: this.configService.getProperty<string>('CONTENTFUL_URL'),
      insecure: false,
    });
  }

  getContent<T>(contentType: ContentTypesEnumType, params?: Dictionary<string>): Observable<T> {
    const body = {
      content_type: contentType,
      'fields.id': this._contentfulService.CONTENTFUL_ID,
      ...params,
    };
    return this.onLangChange().pipe(
      startWith({ lang: this._translateService.currentLang }),
      switchMap(({ lang }) => this.getContentEntries<T>({ ...body, locale: lang })),
      map((entry) => get(entry, 'items[0].fields', null))
    );
  }

  public getAllContentfulFields<T>(contentTypeId: string, params?: ContentfulPayloadObject): Observable<Array<T>> {
    const body: ContentfulPayloadObject = this.mapContentfulPayload(contentTypeId, params);

    return this.contentfulCacheService.checkForCachedContent(body).pipe(
      switchMap((cache: EntryCollection<T>) => {
        if (!cache) {
          return from(
            defer(() =>
              this.contentfulClient
                .getEntries<T>(body)
                .then((response: EntryCollection<T>) => (this.contentfulCacheService.set(body, response), response))
            )
          );
        }
        return of(cache);
      }),
      map((response: EntryCollection<T>) => response.items.map<T>((item: Entry<T>) => get(item, 'fields', null)))
    );
  }
  private mapContentfulPayload(contentTypeId: string, params?: ContentfulPayloadObject): ContentfulPayloadObject {
    this.onLangChange()
      .pipe()
      .subscribe((data) => this.detectLanguageChange$.next(data.lang));
    return {
      content_type: contentTypeId,
      'fields.id': this._contentfulService.CONTENTFUL_ID,
      locale: this._translateService.currentLang,
      ...params,
    };
  }

  onLangChange(): Observable<LangChangeEvent> {
    return this._translateService.onLangChange;
  }

  getContentEntries<T>(query: object): Observable<EntryCollection<T>> {
    return from(this._contentfulService.contentfulClient.getEntries<T>(query));
  }

  public parseContentfulDynamicContent(input, ...params) {
    if (input && params.length > 0) {
      let result = input;
      const regexStr = /\${([^}]+)}/;
      params.forEach((value) => {
        result = result.replace(regexStr, value);
      });
      return result;
    }
    return input;
  }

  public getErrorMessages(): void {
    this.getAllContentfulFields<ErrorContent>(ContentTypesEnum.genericError)
      .pipe()
      .subscribe((response: ErrorContent[]) => {
        this._store.dispatch(
          errorModalActions.setErrorMessagesList({
            defaultContent: response.map((item: ErrorContent) => ({
              body: item.body,
              code: item.code,
              header: item.header
            })) as ErrorModalContent[]
          })
        );
      });
  }

  private setGenericErrorMessage(response: ErrorContent[]) {
    const genericError: ErrorContent = response.find((item: ErrorContent) => item.code === ContentFieldsEnum.Generic);
    this._store.dispatch(
      errorModalActions.setErrorMessage({
        errorToShow: { body: genericError?.body, header: genericError?.header, code: genericError.code } as ErrorModalContent,
      })
    );
  }
}
